import { ArrayUtils, Graph } from 'ts-utils';
import {
  PLAN_INTERVALS,
  PurchaseItem,
  purchaseItemFromQueryParam,
} from '../businessLogic/PlansData';
import { ChoiceItem } from '../components/flows/layouts/SingleSelectLayout';
import { FlowConfig } from '../config/FlowTypes';
import { PurchaseSelector } from '../store/purchase/PurchaseActions';
import { PurchaseState } from '../store/purchase/PurchaseState';
import { Analytics } from './Analytics';

const OTHER_CHOICE = 'Other';

/** Randomize array in-place using the Fisher-Yates algorithm */
export function shuffleChoices(choices: ChoiceItem[]): ChoiceItem[] {
  // Remove 'Other' before shuffling, so we can add it back at the end
  const choicesToShuffle = choices.filter((choice) => choice !== OTHER_CHOICE);

  ArrayUtils.shuffle(choicesToShuffle);

  if (choicesToShuffle.length < choices.length) {
    choicesToShuffle.push(OTHER_CHOICE);
  }
  return choicesToShuffle;
}

/** Construct a graph of screens for a flow configuration. */
export function makeScreensGraph<ScreenNames extends string>(
  screens: FlowConfig<ScreenNames>['screens']
): Graph<ScreenNames> {
  const graph = new Graph<ScreenNames>();

  for (const screen of screens) {
    if (screen.nextScreen === undefined) {
      graph.add({ from: screen.screenName, to: undefined });
      continue;
    }
    if (typeof screen.nextScreen === 'string') {
      graph.add({ from: screen.screenName, to: screen.nextScreen });
    } else {
      for (const to of Object.values(screen.nextScreen)) {
        graph.add({ from: screen.screenName, to });
      }
    }
  }
  return graph;
}

export function trackFlowEnteredAnalytics(purchase: PurchaseState, configName: string): void {
  const hasPlanQueryParam = purchaseItemFromQueryParam() !== undefined;
  // Prepend `default` to identify when a plan variant was not set explicitly by a query param.
  const defaultPrefixStr = hasPlanQueryParam ? '' : 'default-';
  const trialPeriodStr = PurchaseSelector.hasTrial(purchase)
    ? `${purchase.trialPeriodDays}-day-trial`
    : '';
  const planVariant = legacyPlanVariant(purchase);

  const flow = `${defaultPrefixStr}${trialPeriodStr}`;
  const plan = `${defaultPrefixStr}${planVariant}`;

  Analytics.track({ event: Analytics.Event.o_flow_entered, flow, plan, config: configName });
}

/** @deprecated PlanVariant should no longer be used */
function legacyPlanVariant(purchase: PurchaseItem): string {
  const intervalMonths = PLAN_INTERVALS[purchase.plan].inMonths;
  return `${purchase.trialPeriodDays}d${intervalMonths}mth`;
}

/**
 * Convert a list of choice items which may contain a label to a list of just labels
 */
export function choicesToLabels(choices: undefined | ChoiceItem[]): string[] {
  return choices?.map((value) => (typeof value === 'string' ? value : value.label)) ?? [];
}

export function openUrlInNewTab(url: string): void {
  const encodedUrl = encodeURI(url);
  const win = window.open(encodedUrl, '_blank');
  if (win) {
    win.focus();
  } else {
    window.open(encodedUrl, '_blank');
  }
}
